// Server-synchronised clock.
//
// Countdowns must not trust the device clock (users with a skewed clock would
// see the wrong remaining time, and long-lived tabs drift). We sample the
// database clock via the `server_now()` RPC, keep the offset between server
// and device time, and re-sync periodically / when the tab regains focus.
import { supabase } from "@/integrations/supabase/client";

let offsetMs = 0;
let synced = false;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

/** Current best estimate of server time in ms since epoch. */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

export function serverClockOffsetMs(): number {
  return offsetMs;
}

export function isServerClockSynced(): boolean {
  return synced;
}

export function syncServerTime(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const sentAt = Date.now();
      const { data, error } = await supabase.rpc("server_now" as never);
      if (error || !data) return;
      const receivedAt = Date.now();
      const roundTrip = receivedAt - sentAt;
      // Assume symmetric latency: server timestamp corresponds to sentAt + rtt/2.
      const serverMs = new Date(data as unknown as string).getTime();
      if (!Number.isFinite(serverMs)) return;
      offsetMs = serverMs - (sentAt + roundTrip / 2);
      synced = true;
      listeners.forEach((l) => l());
    } catch {
      /* keep the previous offset; countdowns fall back to device time */
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Subscribe to offset changes (so countdowns can re-render after a sync). */
export function onServerTimeSync(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

let started = false;
/** Idempotent: start periodic re-sync. Safe to call from many components. */
export function startServerTimeSync() {
  if (started || typeof window === "undefined") return;
  started = true;
  void syncServerTime();
  window.setInterval(() => void syncServerTime(), 5 * 60_000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void syncServerTime();
  });
}

/** Remaining milliseconds until `endIso`, measured on the server clock. */
export function msUntil(endIso: string): number {
  return new Date(endIso).getTime() - serverNow();
}
